import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Chip, Grid, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchAdmin, toErrorMessage } from '../../admin/api';
import { PATH_DASHBOARD } from '../../routes/paths';
import { StatCard, AdminRouteLayout, RequestState } from './AdminRoutePageShared';
import { AdminDataTable } from '../../sections/admin/shared';
import AdjustmentDetailsDrawer from '../../sections/admin/adjustments/AdjustmentDetailsDrawer';
import {
  STATUS_TABS,
  formatAdjustmentAmount,
  formatAdjustmentType,
  formatValue,
  matchesStatusFilter,
} from '../../sections/admin/adjustments/adjustmentsShared';
import { fDateTime } from '../../utils/formatTime';

function OverviewCards({ overview }) {
  const cards = [
    {
      title: 'Total Requests',
      value: overview?.total_requests ?? 0,
      caption: 'All adjustment requests returned by the backend.',
    },
    {
      title: 'Pending Approval',
      value: overview?.pending_approval ?? 0,
      caption: 'Requests waiting for an approval decision.',
      status: 'pending_approval',
    },
    {
      title: 'Awaiting Execution',
      value: overview?.awaiting_execution ?? 0,
      caption: 'Approved requests ready for execution.',
      status: 'awaiting_execution',
    },
    {
      title: 'Executing',
      value: overview?.executing ?? 0,
      caption: 'Transient backend execution state.',
      status: 'executing',
    },
    {
      title: 'Executed',
      value: overview?.executed ?? 0,
      caption: 'Requests fully executed.',
      status: 'executed',
    },
    {
      title: 'Rejected / Cancelled',
      value: (overview?.rejected ?? 0) + (overview?.cancelled ?? 0),
      caption: 'Final requests that no longer permit actions.',
      status: 'cancelled',
    },
    {
      title: 'Credit vs Debit',
      value: `${overview?.total_credit_requests ?? 0} / ${overview?.total_debit_requests ?? 0}`,
      caption: 'Credit requests first, debit requests second.',
    },
    {
      title: 'With Attachments',
      value: overview?.requests_with_attachments ?? 0,
      caption: 'Requests that include supporting files.',
    },
  ];

  return (
    <Grid container spacing={2.5}>
      {cards.map((card) => (
        <Grid item xs={12} sm={6} xl={3} key={card.title}>
          <StatCard
            title={card.title}
            value={card.value}
            caption={card.caption}
            status={card.status}
          />
        </Grid>
      ))}
    </Grid>
  );
}

OverviewCards.propTypes = {
  overview: PropTypes.shape({
    awaiting_execution: PropTypes.number,
    cancelled: PropTypes.number,
    executed: PropTypes.number,
    executing: PropTypes.number,
    pending_approval: PropTypes.number,
    rejected: PropTypes.number,
    requests_with_attachments: PropTypes.number,
    total_credit_requests: PropTypes.number,
    total_debit_requests: PropTypes.number,
    total_requests: PropTypes.number,
  }),
};

export default function AdjustmentsRequestsPage() {
  const navigate = useNavigate();
  const { requestId } = useParams();
  const [state, setState] = useState({
    loading: false,
    error: null,
    data: {
      items: [],
      overview: null,
    },
  });
  const [selectedTab, setSelectedTab] = useState('all');
  const [selectedRequestId, setSelectedRequestId] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);

  const loadPage = useCallback(async () => {
    setState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      const [overviewResponse, listResponse] = await Promise.all([
        fetchAdmin('/adjustments/overview'),
        fetchAdmin('/adjustments'),
      ]);

      setState({
        loading: false,
        error: null,
        data: {
          overview: overviewResponse?.overview || null,
          items: listResponse?.items || [],
        },
      });
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: toErrorMessage(error),
      }));
    }
  }, []);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    if (requestId) {
      setSelectedRequestId(requestId);
      setDrawerOpen(true);
      return;
    }

    setDrawerOpen(false);
    setSelectedRequestId('');
  }, [requestId]);

  const filteredItems = useMemo(
    () =>
      (state.data?.items || []).filter((row) =>
        matchesStatusFilter(row, selectedTab)
      ),
    [selectedTab, state.data]
  );

  const rows = useMemo(
    () =>
      filteredItems.map((row) => ({
        id: row.id,
        requestId: row.request_id,
        adjustmentType: formatAdjustmentType(row.adjustment_type),
        userId: row.user_id || 'N/A',
        direction: row.direction || 'N/A',
        status: row.status || 'N/A',
        amount: formatAdjustmentAmount(row),
        reasonCode: row.reason_code || 'N/A',
        requestedBy: row.requested_by || 'N/A',
        createdAt: row.created_at ? fDateTime(row.created_at) : 'N/A',
        attachmentState: row.image_urls?.length ? `${row.image_urls.length} file(s)` : 'None',
      })),
    [filteredItems]
  );

  return (
    <AdminRouteLayout title="Requests" breadcrumbTitle="Adjustments">
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Stack spacing={0.75}>
            <Typography variant="h4">Adjustment Requests</Typography>
            <Typography variant="body2" color="text.secondary">
              Review adjustment lifecycle state, open detail in a side drawer, and keep approval and execution work auditable.
            </Typography>
          </Stack>
          <Button
            variant="contained"
            onClick={() => navigate(PATH_DASHBOARD.adjustments.create)}
          >
            Create Adjustment
          </Button>
        </Stack>

        <RequestState loading={state.loading} error={state.error}>
          <Stack spacing={3}>
            <OverviewCards overview={state.data?.overview} />

            <Box
              sx={{
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Tabs
                value={selectedTab}
                onChange={(_, value) => setSelectedTab(value)}
                variant="scrollable"
                scrollButtons="auto"
              >
                {STATUS_TABS.map((tab) => (
                  <Tab key={tab.value} value={tab.value} label={tab.label} />
                ))}
              </Tabs>
            </Box>

            <AdminDataTable
              title="Adjustment Request List"
              description="The backend currently returns an unpaginated newest-first array. Open a request to inspect attachments, approval history, and allowed actions."
              columns={[
                {
                  field: 'requestId',
                  headerName: 'Request ID',
                  render: (row) => (
                    <Button
                      variant="text"
                      onClick={() => {
                        navigate(PATH_DASHBOARD.adjustments.details(row.requestId));
                      }}
                      sx={{ px: 0, fontFamily: 'monospace' }}
                    >
                      {row.requestId}
                    </Button>
                  ),
                },
                { field: 'adjustmentType', headerName: 'Type' },
                { field: 'userId', headerName: 'User ID' },
                {
                  field: 'direction',
                  headerName: 'Direction',
                  render: (row) => (
                    <Chip
                      label={formatValue(row.direction)}
                      size="small"
                      color={row.direction === 'credit' ? 'success' : 'warning'}
                      variant="outlined"
                    />
                  ),
                },
                { field: 'status', headerName: 'Status' },
                { field: 'amount', headerName: 'Amount / Target' },
                { field: 'reasonCode', headerName: 'Reason Code' },
                { field: 'requestedBy', headerName: 'Requested By' },
                { field: 'createdAt', headerName: 'Created' },
                { field: 'attachmentState', headerName: 'Attachments' },
              ]}
              rows={rows}
              emptyTitle="No adjustment requests"
              emptyDescription="The backend returned no requests for the selected status view."
            />
          </Stack>
        </RequestState>
      </Stack>

      <AdjustmentDetailsDrawer
        open={drawerOpen}
        requestId={selectedRequestId}
        onClose={() => navigate(PATH_DASHBOARD.adjustments.list)}
        onRequestUpdated={loadPage}
      />
    </AdminRouteLayout>
  );
}
